import { useState, useRef } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Trash2, Plus } from "lucide-react";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Form } from "../../components/Form";
import { TextArea } from "../../components/TextArea";
import { FormGroup } from "../../components/FormGroup";
import { FormLabel } from "../../components/FormLabel";
import { FormActions } from "../../components/FormActions";
import { SelectInput } from "../../components/SelectInput";
import { Toggle } from "../../components/Toggle";
import { MultiCurrencyMoneyInput } from "../../components/MultiCurrencyMoneyInput";
import { spacing } from "@styling/sizing";
import colors from "@styling/colors";
import { fontSizes } from "@styling/fonts";
import type {
  RestAvailableBillingItem,
} from "@KassaroAB/shared/types/availableBillingItem";
import { BillingItemType } from "@KassaroAB/shared/types/enums/BillingItemType";
import { Features } from "@KassaroAB/shared/types/enums/Features";
import { PaymentProvider } from "@KassaroAB/shared/types/enums/PaymentProvider";
import type { MultiCurrencyMoney } from "@KassaroAB/shared/types/multiCurrencyMoney";
import { zeroMultiCurrencyMoney } from "@KassaroAB/shared/utils/multiCurrencyMoney";

const HardwareCategory = {
  OTHER: "other",
  HANDHELD: "handheld",
  TABLET: "tablet",
  DESKTOP: "desktop",
} as const;

const NarrowFormGroup = styled(FormGroup)`
  max-width: 220px;
`;

const PriceLabel = styled(FormLabel)`
  font-weight: 600;
  margin-bottom: ${spacing.x1}px;
`;

const FileInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x1}px;
`;

const FileNameDisplay = styled.div`
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
  margin-top: ${spacing.x05}px;
`;

const MAX_IMAGE_SIZE = 800;

const ImagePreview = styled.img`
  max-width: ${MAX_IMAGE_SIZE}px;
  max-height: ${MAX_IMAGE_SIZE}px;
  margin-top: ${spacing.x1}px;
  border-radius: 4px;
`;

const resizeImage = (file: File, maxSize: number): Promise<File> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width <= maxSize && height <= maxSize) {
        resolve(file);
        return;
      }
      const scale = Math.min(maxSize / width, maxSize / height);
      width = Math.round(width * scale);
      height = Math.round(height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(file);
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(new File([blob], file.name, { type: file.type }));
          } else {
            resolve(file);
          }
        },
        file.type,
        0.9
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };
    img.src = url;
  });
};

const BulletsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
`;

const BulletItem = styled.div`
  display: flex;
  gap: ${spacing.x2}px;
  align-items: center;
`;

const BulletInput = styled(Input)`
  flex: 1;
`;

const RemoveButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: ${colors.textMuted};
  cursor: pointer;
  padding: ${spacing.x05}px;
  border-radius: 4px;
  min-width: auto;

  &:hover {
    background-color: ${colors.backgroundDark};
    color: ${colors.danger};
  }
`;

const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: ${colors.textMuted};
  cursor: pointer;
  padding: ${spacing.x05}px;
  border-radius: 4px;
  min-width: auto;
  flex-shrink: 0;

  &:hover {
    background-color: ${colors.backgroundDark};
    color: ${colors.text};
  }
`;

const PaymentTerminalsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.x2}px;
  align-items: center;
`;

const PaymentTerminalRow = styled.div`
  display: flex;
  gap: ${spacing.x1}px;
  align-items: center;
`;

const PaymentProviderSelect = styled(SelectInput)`
  width: 180px;
`;

const PaymentTerminalCountInput = styled(Input)`
  width: 100px;
`;

const FeatureCheckboxGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: ${spacing.x1}px;
  max-width: 100%;
  min-width: 0;
`;

const FeatureCheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${spacing.x1}px;
  font-size: ${fontSizes.regular}px;
  color: ${colors.text};
  cursor: pointer;
  min-width: 0;
  overflow-wrap: break-word;
`;

const FeatureCheckbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

interface AvailableBillingItemFormProps {
  availableBillingItem?: RestAvailableBillingItem;
  onSubmit: (formData: FormData) => void;
  onCancel: () => void;
}

export const AvailableBillingItemForm = ({
  availableBillingItem,
  onSubmit,
  onCancel,
}: AvailableBillingItemFormProps) => {
  const { t } = useTranslation();
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(availableBillingItem?.image || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const HARDWARE_CATEGORIES = [HardwareCategory.OTHER, HardwareCategory.HANDHELD, HardwareCategory.TABLET, HardwareCategory.DESKTOP] as const;

  const form = useForm({
    defaultValues: {
      type: availableBillingItem?.type || "",
      name: availableBillingItem?.name || "",
      description: availableBillingItem?.description || "",
      bullets: availableBillingItem?.bullets || [],
      comingSoon: availableBillingItem?.comingSoon ?? false,
      selectedFeatures:
        availableBillingItem?.grants?.features ??
        availableBillingItem?.features ??
        [],
      grantsAdditionalDevices:
        availableBillingItem?.grants?.additionalDevices ?? 0,
      grantedPaymentTerminals: Object.entries(
        availableBillingItem?.grants?.paymentTerminals ?? {}
      ).map(([provider, count]) => ({
        provider: provider as PaymentProvider,
        count: count ?? 0,
      })),
      category: availableBillingItem?.category || "",
      monthlyPrice:
        availableBillingItem?.pricing?.monthlyPrice || zeroMultiCurrencyMoney(),
      oneTimePrice:
        availableBillingItem?.pricing?.oneTimePrice || zeroMultiCurrencyMoney(),
      prepaid36MonthPrice:
        availableBillingItem?.pricing?.prepaid36MonthPrice ||
        zeroMultiCurrencyMoney(),
    },
    onSubmit: ({ value }) => {
      const formData = new FormData();
      formData.append("type", value.type);
      formData.append("name", value.name);
      formData.append("description", value.description);
      formData.append("comingSoon", value.comingSoon.toString());

      if (value.bullets.length > 0) {
        formData.append(
          "bullets",
          JSON.stringify(value.bullets.filter((b) => b.trim() !== ""))
        );
      }
      if (
        value.type === BillingItemType.PACKAGE ||
        value.type === BillingItemType.ADD_ON
      ) {
        formData.append("features", JSON.stringify(value.selectedFeatures));
      }
      formData.append(
        "grants",
        JSON.stringify({
          features: value.selectedFeatures,
          additionalDevices: value.grantsAdditionalDevices,
          paymentTerminals: Object.fromEntries(
            value.grantedPaymentTerminals.map((row) => [row.provider, row.count])
          ),
        })
      );
      if (value.type === BillingItemType.HARDWARE && value.category) {
        formData.append("category", value.category);
      }
      const pricing: Record<string, MultiCurrencyMoney> = {};
      if (
        Object.keys(value.monthlyPrice.currencies).length > 0 &&
        Object.values(value.monthlyPrice.currencies).some((c) => c.amount > 0)
      ) {
        pricing.monthlyPrice = value.monthlyPrice;
      }
      if (
        Object.keys(value.oneTimePrice.currencies).length > 0 &&
        Object.values(value.oneTimePrice.currencies).some((c) => c.amount > 0)
      ) {
        pricing.oneTimePrice = value.oneTimePrice;
      }
      if (
        Object.keys(value.prepaid36MonthPrice.currencies).length > 0 &&
        Object.values(value.prepaid36MonthPrice.currencies).some(
          (c) => c.amount > 0
        )
      ) {
        pricing.prepaid36MonthPrice = value.prepaid36MonthPrice;
      }
      formData.append("pricing", JSON.stringify(pricing));

      if (imageFile) {
        formData.append("image", imageFile);
      }

      onSubmit(formData);
    },
  });

  const type = useStore(form.store, (state) => state.values.type);
  const bullets = useStore(form.store, (state) => state.values.bullets);
  const selectedFeatures = useStore(
    form.store,
    (state) => state.values.selectedFeatures
  );
  const grantedPaymentTerminals = useStore(
    form.store,
    (state) => state.values.grantedPaymentTerminals
  );

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      setImageFile(null);
      setImagePreview(availableBillingItem?.image || null);
      return;
    }
    resizeImage(file, MAX_IMAGE_SIZE)
      .then((resizedFile) => {
        setImageFile(resizedFile);
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result as string);
        };
        reader.readAsDataURL(resizedFile);
      })
      .catch(() => {
        setImageFile(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result as string);
        };
        reader.readAsDataURL(file);
      });
  };

  const handleBulletChange = (index: number, value: string) => {
    form.setFieldValue("bullets", bullets.map((bullet, i) => (i === index ? value : bullet)));
  };

  const handleAddBullet = () => {
    form.setFieldValue("bullets", [...bullets, ""]);
  };

  const handleRemoveBullet = (index: number) => {
    form.setFieldValue(
      "bullets",
      bullets.filter((_, i) => i !== index)
    );
  };

  const handleFeatureToggle = (feature: Features) => {
    form.setFieldValue(
      "selectedFeatures",
      selectedFeatures.includes(feature)
        ? selectedFeatures.filter((f) => f !== feature)
        : [...selectedFeatures, feature]
    );
  };

  const availablePaymentProviders = Object.values(PaymentProvider).filter(
    (paymentProvider) =>
      !grantedPaymentTerminals.some((row) => row.provider === paymentProvider)
  );

  const handleAddPaymentTerminal = () => {
    const nextProvider = availablePaymentProviders[0];
    if (!nextProvider) return;
    form.setFieldValue("grantedPaymentTerminals", [
      ...grantedPaymentTerminals,
      { provider: nextProvider, count: 1 },
    ]);
  };

  const handlePaymentTerminalProviderChange = (
    index: number,
    provider: PaymentProvider
  ) => {
    form.setFieldValue(
      "grantedPaymentTerminals",
      grantedPaymentTerminals.map((row, rowIndex) =>
        rowIndex === index ? { ...row, provider } : row
      )
    );
  };

  const handlePaymentTerminalCountChange = (index: number, value: string) => {
    const parsedCount = parseInt(value, 10);
    form.setFieldValue(
      "grantedPaymentTerminals",
      grantedPaymentTerminals.map((row, rowIndex) =>
        rowIndex === index
          ? {
              ...row,
              count: isNaN(parsedCount) ? 0 : Math.max(0, parsedCount),
            }
          : row
      )
    );
  };

  const handleRemovePaymentTerminal = (index: number) => {
    form.setFieldValue(
      "grantedPaymentTerminals",
      grantedPaymentTerminals.filter((_, rowIndex) => rowIndex !== index)
    );
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <form.Field name="type">
        {(field) => (
          <FormGroup>
            <FormLabel>{t("availableBillingItemForm.type")}</FormLabel>
            <SelectInput
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
            >
              <option value="">{t("availableBillingItemForm.selectType")}</option>
              {Object.values(BillingItemType).map((itemType) => (
                <option key={itemType} value={itemType}>
                  {t(`billingItems.type.${itemType}`)}
                </option>
              ))}
            </SelectInput>
          </FormGroup>
        )}
      </form.Field>

      <form.Field name="name">
        {(field) => (
          <FormGroup>
            <FormLabel>{t("availableBillingItemForm.name")}</FormLabel>
            <Input
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
            />
          </FormGroup>
        )}
      </form.Field>

      <form.Field name="description">
        {(field) => (
          <FormGroup>
            <FormLabel>{t("availableBillingItemForm.description")}</FormLabel>
            <TextArea
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
            />
          </FormGroup>
        )}
      </form.Field>

      <form.Field name="comingSoon">
        {(field) => (
          <FormGroup>
            <FormLabel>{t("availableBillingItemForm.comingSoon")}</FormLabel>
            <Toggle
              checked={field.state.value}
              onChange={field.handleChange}
              label={t("availableBillingItemForm.comingSoonLabel")}
            />
          </FormGroup>
        )}
      </form.Field>

      {type === BillingItemType.HARDWARE && (
        <form.Field name="category">
          {(field) => (
            <NarrowFormGroup>
              <FormLabel>{t("availableBillingItemForm.categoryOptional")}</FormLabel>
              <SelectInput
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
              >
                <option value="">{t("availableBillingItemForm.selectCategory")}</option>
                {HARDWARE_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {t(`billingItems.hardware.category.${cat}`)}
                  </option>
                ))}
              </SelectInput>
            </NarrowFormGroup>
          )}
        </form.Field>
      )}

      <FormGroup>
        <FormLabel>{t("availableBillingItemForm.bulletsOptional")}</FormLabel>
        <BulletsContainer>
          {bullets.map((bullet, index) => (
            <BulletItem key={index}>
              <BulletInput
                type="text"
                value={bullet}
                onChange={(e) => handleBulletChange(index, e.target.value)}
                placeholder={t("availableBillingItemForm.bulletPoint", { index: index + 1 })}
              />
              <RemoveButton
                type="button"
                aria-label={t("availableBillingItemForm.remove")}
                onClick={() => handleRemoveBullet(index)}
              >
                <Trash2 size={16} />
              </RemoveButton>
            </BulletItem>
          ))}
          <AddButton
            type="button"
            aria-label={t("availableBillingItemForm.addBullet")}
            onClick={handleAddBullet}
          >
            <Plus size={16} />
          </AddButton>
        </BulletsContainer>
      </FormGroup>

      <FileInputContainer>
        <FormGroup>
          <FormLabel>{t("availableBillingItemForm.image")}</FormLabel>
          <Input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageChange}
          />
          {imageFile && (
            <FileNameDisplay>
              {t("availableBillingItemForm.selectedImage", {
                name: imageFile.name,
                size: (imageFile.size / 1024).toFixed(2),
              })}
            </FileNameDisplay>
          )}
          {imagePreview && (
            <ImagePreview src={imagePreview} alt={t("availableBillingItemForm.imagePreviewAlt")} />
          )}
        </FormGroup>
      </FileInputContainer>

      {(type === BillingItemType.PACKAGE || type === BillingItemType.ADD_ON) && (
        <FormGroup>
          <FormLabel>{t("availableBillingItemForm.features")}</FormLabel>
          <FeatureCheckboxGroup>
            {Object.values(Features).map((feature) => (
              <FeatureCheckboxLabel key={feature}>
                <FeatureCheckbox
                  type="checkbox"
                  checked={selectedFeatures.includes(feature)}
                  onChange={() => handleFeatureToggle(feature)}
                />
                {t(`availableBillingItemForm.feature.${feature}`)}
              </FeatureCheckboxLabel>
            ))}
          </FeatureCheckboxGroup>
        </FormGroup>
      )}

      <FormGroup>
        <PriceLabel>{t("availableBillingItemForm.grants")}</PriceLabel>
        <form.Field name="grantsAdditionalDevices">
          {(field) => (
            <NarrowFormGroup>
              <FormLabel>
                {t("availableBillingItemForm.grantsAdditionalDevices")}
              </FormLabel>
              <Input
                type="number"
                min="0"
                value={field.state.value}
                onChange={(e) => {
                  const parsedDevices = parseInt(e.target.value, 10);
                  field.handleChange(
                    isNaN(parsedDevices) ? 0 : Math.max(0, parsedDevices)
                  );
                }}
                onBlur={field.handleBlur}
              />
            </NarrowFormGroup>
          )}
        </form.Field>
        <FormLabel>
          {t("availableBillingItemForm.grantsPaymentTerminals")}
        </FormLabel>
        <PaymentTerminalsContainer>
          {grantedPaymentTerminals.map((row, index) => {
            const providerOptions = [
              row.provider,
              ...availablePaymentProviders,
            ];
            return (
              <PaymentTerminalRow key={`${row.provider}-${index}`}>
                <PaymentProviderSelect
                  value={row.provider}
                  onChange={(e) =>
                    handlePaymentTerminalProviderChange(
                      index,
                      e.target.value as PaymentProvider
                    )
                  }
                >
                  {providerOptions.map((paymentProvider) => (
                    <option key={paymentProvider} value={paymentProvider}>
                      {t(
                        `availableBillingItemForm.paymentProvider.${paymentProvider}`
                      )}
                    </option>
                  ))}
                </PaymentProviderSelect>
                <PaymentTerminalCountInput
                  type="number"
                  min="0"
                  value={row.count}
                  onChange={(e) =>
                    handlePaymentTerminalCountChange(index, e.target.value)
                  }
                  placeholder={t(
                    "availableBillingItemForm.grantsPaymentTerminalCount"
                  )}
                />
                <RemoveButton
                  type="button"
                  aria-label={t("availableBillingItemForm.remove")}
                  onClick={() => handleRemovePaymentTerminal(index)}
                >
                  <Trash2 size={16} />
                </RemoveButton>
              </PaymentTerminalRow>
            );
          })}
          {availablePaymentProviders.length > 0 && (
            <AddButton
              type="button"
              aria-label={t("availableBillingItemForm.addPaymentTerminal")}
              onClick={handleAddPaymentTerminal}
            >
              <Plus size={16} />
            </AddButton>
          )}
        </PaymentTerminalsContainer>
      </FormGroup>

      <form.Field name="monthlyPrice">
        {(field) => (
          <MultiCurrencyMoneyInput
            label={t("availableBillingItemForm.monthlyPriceOptional")}
            value={field.state.value}
            onChange={field.handleChange}
            amountPlaceholder={t("availableBillingItemForm.amountPlaceholder")}
          />
        )}
      </form.Field>

      <form.Field name="oneTimePrice">
        {(field) => (
          <MultiCurrencyMoneyInput
            label={t("availableBillingItemForm.oneTimePriceOptional")}
            value={field.state.value}
            onChange={field.handleChange}
            amountPlaceholder={t("availableBillingItemForm.amountPlaceholder")}
          />
        )}
      </form.Field>

      <form.Field name="prepaid36MonthPrice">
        {(field) => (
          <MultiCurrencyMoneyInput
            label={t("availableBillingItemForm.prepaid36MonthPriceOptional")}
            value={field.state.value}
            onChange={field.handleChange}
            amountPlaceholder={t("availableBillingItemForm.amountPlaceholder")}
          />
        )}
      </form.Field>

      <FormActions>
        <Button type="button" $color="secondary" onClick={onCancel}>
          {t("availableBillingItemForm.cancel")}
        </Button>
        <Button type="submit">
          {availableBillingItem ? t("availableBillingItemForm.update") : t("availableBillingItemForm.create")}
        </Button>
      </FormActions>
    </Form>
  );
};
